// Audio procedural con Web Audio API. Sin archivos: sintetiza efectos.
// Filosofía: nada de "beeps" de oscilador pelado. Los efectos se construyen con
//  (1) transitorios de RUIDO filtrado -> "clics" físicos de madera/fichas, y
//  (2) tonos tipo MARIMBA (fundamental + armónico suave, envolvente percusiva,
//      filtro paso-bajo) -> notas cálidas, no sintéticas.
import { EVENTS } from '../utils/constants.js';

export class AudioManager {
  constructor(bus, config) {
    this.bus = bus;
    this.cfg = config.audio;
    this.ctx = null;
    this.master = null;
    this.musicGain = null;
    this.sfxGain = null;
    this._musicNodes = [];
    this._noise = null;

    // Reacciona a eventos de sonido.
    this.bus.on(EVENTS.PLAY_SFX, ({ name }) => this.play(name));
    this.bus.on(EVENTS.VIBRATE, ({ ms }) => this.vibrate(ms));
    // Reproduce sonidos automáticos de eventos de juego.
    this.bus.on(EVENTS.TURN_CONFIRMED, () => this.play('word'));
    this.bus.on(EVENTS.TURN_INVALID, () => this.play('invalid'));
    this.bus.on(EVENTS.GAME_END, () => this.play('victory'));
  }

  /** Debe llamarse tras una interacción del usuario (política de autoplay). */
  unlock() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.cfg.muted ? 0 : this.cfg.master;
    this.master.connect(this.ctx.destination);

    // Compresor suave: pega los transitorios sin saturar al encadenar efectos.
    this.comp = this.ctx.createDynamicsCompressor();
    this.comp.threshold.value = -18; this.comp.knee.value = 24;
    this.comp.ratio.value = 3; this.comp.attack.value = 0.003; this.comp.release.value = 0.18;
    this.comp.connect(this.master);

    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = this.cfg.sfx;
    this.sfxGain.connect(this.comp);

    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = this.cfg.music;
    this.musicGain.connect(this.master);

    // Arranca la música ambiental en bucle (fondo relajante) en cuanto hay audio.
    this.startMusic();
  }

  setMuted(muted) {
    this.cfg.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : this.cfg.master;
  }
  setMaster(v) { this.cfg.master = v; if (this.master && !this.cfg.muted) this.master.gain.value = v; }
  setMusic(v) { this.cfg.music = v; if (this.musicGain) this.musicGain.gain.value = v; }
  setSfx(v) { this.cfg.sfx = v; if (this.sfxGain) this.sfxGain.gain.value = v; }

  _out() { return this.sfxGain || this.ctx.destination; }
  _rand(a, b) { return a + Math.random() * (b - a); }

  /** Buffer de ruido blanco reutilizable (para transitorios percusivos). */
  _noiseBuf() {
    if (this._noise) return this._noise;
    const len = Math.floor(this.ctx.sampleRate * 0.4);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this._noise = buf; return buf;
  }

  /**
   * Transitorio de RUIDO filtrado: el "clic" físico (madera/ficha/UI).
   * type: 'bandpass' (madera), 'lowpass' (grave/mate), 'highpass' (chasquido).
   */
  _click(freq = 1200, dur = 0.06, { q = 1, gain = 0.22, type = 'bandpass', delay = 0 } = {}) {
    if (!this.ctx) return;
    const t0 = this.ctx.currentTime + delay;
    const src = this.ctx.createBufferSource();
    src.buffer = this._noiseBuf();
    src.playbackRate.value = this._rand(0.92, 1.08); // varía el timbre: no suena calcado
    const filt = this.ctx.createBiquadFilter();
    filt.type = type; filt.frequency.value = freq; filt.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filt); filt.connect(g); g.connect(this._out());
    src.start(t0); src.stop(t0 + dur + 0.02);
  }

  /**
   * Nota tipo MARIMBA/madera: fundamental (sine) + octava suave (triangle),
   * ataque rápido, caída exponencial y paso-bajo. Cálida, nada "chip".
   */
  _mallet(freq, { dur = 0.32, gain = 0.24, delay = 0, partial = 0.32, lp = 3200, detune = 0, out = null } = {}) {
    if (!this.ctx) return;
    const t0 = this.ctx.currentTime + delay;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass'; lpf.frequency.value = lp; lpf.Q.value = 0.5;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const o1 = this.ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = freq; o1.detune.value = detune;
    const o2 = this.ctx.createOscillator(); o2.type = 'triangle'; o2.frequency.value = freq * 2.01;
    const g2 = this.ctx.createGain(); g2.gain.value = partial;
    o1.connect(g); o2.connect(g2); g2.connect(g);
    g.connect(lpf); lpf.connect(out || this._out());
    o1.start(t0); o2.start(t0); o1.stop(t0 + dur + 0.02); o2.stop(t0 + dur + 0.02);
  }

  // ---------------------------------------------------------------------------
  // Música ambiental relajante EN BUCLE (procedural, sin ficheros).
  // Progresión lenta de acordes con "pads" suaves + arpegios cálidos, muy bajita,
  // pensada como fondo. Suena por `musicGain` (respeta el slider de Música y el silencio).
  // ---------------------------------------------------------------------------
  /** Nota "pad": osciladores muy cálidos, envolvente lentísima (ambient/drone que "respira"). */
  _pad(freq, t0, dur, gain) {
    if (!this.ctx || !this.musicGain) return;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass'; lpf.frequency.value = 950; lpf.Q.value = 0.25; // muy cálido, sin agudos
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 3.6);            // ataque lentísimo (entra suave)
    g.gain.setValueAtTime(gain, Math.max(t0 + 3.6, t0 + dur - 4));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);          // release muy largo (cola)
    const o1 = this.ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = freq;
    const o2 = this.ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = freq; o2.detune.value = 4; // chorus muy suave (sin brillo)
    const g2 = this.ctx.createGain(); g2.gain.value = 0.6;
    o1.connect(g); o2.connect(g2); g2.connect(g); g.connect(lpf); lpf.connect(this.musicGain);
    o1.start(t0); o2.start(t0); o1.stop(t0 + dur + 0.4); o2.stop(t0 + dur + 0.4);
  }

  /** Campana suave (melodía): seno con un armónico tenue y caída larga. Nunca estridente. */
  _bell(freq, t0, gain) {
    if (!this.ctx || !this.musicGain) return;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass'; lpf.frequency.value = 2600; lpf.Q.value = 0.4;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.6); // decae lento (relajante)
    const o1 = this.ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = freq;
    const o2 = this.ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = freq * 2;
    const g2 = this.ctx.createGain(); g2.gain.value = 0.14;
    o1.connect(g); o2.connect(g2); g2.connect(g); g.connect(lpf); lpf.connect(this.musicGain);
    o1.start(t0); o2.start(t0); o1.stop(t0 + 2.8); o2.stop(t0 + 2.8);
  }

  startMusic() {
    if (!this.ctx || !this.musicGain || this._musicPlaying) return;
    this._musicPlaying = true;
    this._musicStep = 0;
    this._scheduleMusic();
  }

  stopMusic() {
    this._musicPlaying = false;
    if (this._musicTimer) { clearTimeout(this._musicTimer); this._musicTimer = null; }
  }

  _scheduleMusic() {
    if (!this._musicPlaying || !this.ctx) return;
    // RELAX: progresión lentísima y consonante (Do). Acordes suaves de 3 voces + bajo.
    // Cambios cada 12 s, con solape (crossfade) → nunca hay cortes, siempre "respira".
    const prog = [
      { pad: [261.63, 329.63, 392.00], bass: 130.81 }, // C
      { pad: [220.00, 293.66, 349.23], bass: 110.00 }, // Am7-ish (A C? -> A D F) suave
      { pad: [174.61, 261.63, 329.63], bass: 87.31 },  // Fmaj7
      { pad: [196.00, 293.66, 392.00], bass: 98.00 },  // Gsus2
    ];
    // Melodía MUY escasa (solo a veces) en pentatónica → sensación de calma, sin agobiar.
    const PENTA = [523.25, 587.33, 659.25, 783.99, 880.00];
    const BAR = 12; // segundos por acorde (muy lento)
    const chord = prog[this._musicStep % prog.length];
    const t0 = this.ctx.currentTime + 0.05;
    // Pad sostenido con solape amplio (dura BAR+5 → se cruza con el siguiente).
    for (const f of chord.pad) this._pad(f, t0, BAR + 5, 0.028);
    this._pad(chord.bass, t0, BAR + 4, 0.05); // bajo grave que ancla
    // Una sola campana suave por acorde, y solo en la mitad de los compases (mucho espacio).
    if (this._musicStep % 2 === 0) {
      const n = PENTA[(this._musicStep * 2) % PENTA.length];
      this._bell(n, t0 + 3, 0.016);
    }
    this._musicStep += 1;
    this._musicTimer = setTimeout(() => this._scheduleMusic(), BAR * 1000);
  }

  /** Reproduce un efecto por nombre. */
  play(name) {
    if (!this.ctx || this.cfg.muted) return;
    const d = this._rand(-8, 8); // micro-detune para que no suene mecánico
    switch (name) {
      // --- SELECCIÓN / UI: clic cálido de madera, no un pitido ---
      case 'button':
        this._click(2600, 0.028, { type: 'highpass', q: 0.7, gain: 0.12 });
        this._mallet(740, { dur: 0.11, gain: 0.07, partial: 0.2, lp: 2600, detune: d });
        break;

      // --- FICHAS ---
      case 'pick': // coger ficha: "tock" de madera
        this._click(1500, 0.05, { type: 'bandpass', q: 1.6, gain: 0.16 });
        this._mallet(this._rand(560, 640), { dur: 0.13, gain: 0.11, partial: 0.5, lp: 3000 });
        break;
      case 'drop': // soltar ficha: "tunk" grave y satisfactorio
        this._click(560, 0.075, { type: 'lowpass', q: 0.8, gain: 0.2 });
        this._mallet(this._rand(280, 320), { dur: 0.2, gain: 0.16, partial: 0.28, lp: 1700 });
        break;
      case 'snap': // encajar/bloquear: clic nítido con leve subida
        this._click(2600, 0.035, { type: 'bandpass', q: 2.2, gain: 0.15 });
        this._mallet(this._rand(820, 900), { dur: 0.1, gain: 0.1, partial: 0.3, lp: 4000 });
        break;

      // --- PALABRAS / RECOMPENSAS: marimba cálida ---
      case 'word': // confirmar palabra: arpegio mayor ascendente
        [523.25, 659.25, 783.99].forEach((f, i) => this._mallet(f, { dur: 0.34, gain: 0.22, delay: i * 0.075, detune: d }));
        break;
      case 'formword': // palabra válida al construir: destello brillante
        [659.25, 987.77, 1318.51].forEach((f, i) => this._mallet(f, { dur: 0.28, gain: 0.18, delay: i * 0.05, lp: 5000 }));
        this._click(6000, 0.05, { type: 'highpass', q: 0.6, gain: 0.05, delay: 0.12 });
        break;
      case 'evolution': // evolucionar: subida mágica + brillo
        [659.25, 880, 1174.66, 1567.98].forEach((f, i) => this._mallet(f, { dur: 0.3, gain: 0.2, delay: i * 0.08, lp: 5200 }));
        break;
      case 'combo': // cadena: arpegio rápido
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this._mallet(f, { dur: 0.2, gain: 0.19, delay: i * 0.06 }));
        break;
      case 'reward': // moneda/premio: campanilla brillante
        [987.77, 1318.51, 1760].forEach((f, i) => this._mallet(f, { dur: 0.22, gain: 0.16, delay: i * 0.07, partial: 0.45, lp: 6000 }));
        break;

      // --- CUENTA ATRÁS: tic de reloj seco en los últimos segundos ---
      case 'tick':
        this._click(2200, 0.028, { type: 'bandpass', q: 3, gain: 0.16 });
        this._mallet(1180, { dur: 0.05, gain: 0.09, partial: 0.2, lp: 5200, detune: d });
        break;

      // --- ERRORES: golpe grave y mate, sin sierra estridente ---
      case 'invalid':
        this._click(180, 0.14, { type: 'lowpass', q: 0.8, gain: 0.22 });
        this._mallet(150, { dur: 0.2, gain: 0.14, partial: 0.15, lp: 700, detune: -20 });
        break;

      // --- FIN DE PARTIDA ---
      case 'victory': // fanfarria cálida
        [523.25, 659.25, 783.99, 1046.5, 1318.51].forEach((f, i) =>
          this._mallet(f, { dur: 0.45, gain: 0.24, delay: i * 0.1, partial: 0.4, lp: 5000 }));
        // acorde final sostenido
        [523.25, 659.25, 783.99].forEach((f) => this._mallet(f, { dur: 0.9, gain: 0.12, delay: 0.5, lp: 4000 }));
        break;
      case 'defeat': // descenso suave, nada agresivo
        [392, 329.63, 261.63].forEach((f, i) =>
          this._mallet(f, { dur: 0.5, gain: 0.18, delay: i * 0.16, partial: 0.2, lp: 1600 }));
        break;

      default: break;
    }
  }

  /** Vibración opcional (móvil). */
  vibrate(ms = 20) {
    if (this.cfg.vibration && navigator.vibrate) navigator.vibrate(ms);
  }
}
